"use client";

import { useQuery } from "@tanstack/react-query";
import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import useIsAuthorizedToSeeDashboards from "./useIsAuthorizedToSeeDashboards";

import {
  type Comedian,
  getComediansByIDs,
} from "@punchup/data/legacy/comedian";
import { type VenuePagesRow, getVenuePagesByIDs } from "../../data/venue_pages";

interface AdministeredAccounts {
  comedians: Comedian[];
  venuePages: VenuePagesRow[];
}

interface UseAdministeredAccountsResult extends AdministeredAccounts {
  isLoading: boolean;
}

// Stable identities, so a consumer's useMemo does not re-run on every render
// that happens before the rows land.
const EMPTY: AdministeredAccounts = { comedians: [], venuePages: [] };

/** The key prefix of every administered-accounts query. Invalidate it after an edit to one of these rows. */
export const ADMINISTERED_ACCOUNTS_QUERY_KEY = "administered-accounts";

/**
 * Every page the signed-in user administers (comedians and venue pages) on ONE
 * query key, so every dashboard surface shares a single fetch per table.
 */
export function useAdministeredAccounts(): UseAdministeredAccountsResult {
  const {
    comediansAuthorized,
    venuesAuthorized,
    isLoading: accountIdsLoading,
  } = useIsAuthorizedToSeeDashboards();

  // Sorted so two callers holding the same ids in a different order still share
  // one query: React Query hashes the key structurally, so order is part of it.
  const comedianIds = [...comediansAuthorized].sort();
  const venueIds = [...venuesAuthorized].sort();
  const hasAccountIds = comedianIds.length + venueIds.length > 0;

  const { data, isPending } = useQuery({
    queryKey: [ADMINISTERED_ACCOUNTS_QUERY_KEY, comedianIds, venueIds],
    queryFn: async (): Promise<AdministeredAccounts> => {
      const supabase = createClientComponentClient<Database>();
      const [comedians, venuePages] = await Promise.all([
        getComediansByIDs(comedianIds),
        getVenuePagesByIDs(supabase, venueIds),
      ]);
      return { comedians, venuePages };
    },
    enabled: hasAccountIds,
    staleTime: 5 * 60 * 1000,
  });

  return {
    comedians: data?.comedians ?? EMPTY.comedians,
    venuePages: data?.venuePages ?? EMPTY.venuePages,
    // Both phases count as loading: empty ids look exactly like "administers
    // nothing" until the authorization lookup settles. `isPending` is also true
    // for a query that never runs, hence the `hasAccountIds` guard.
    isLoading: accountIdsLoading || (hasAccountIds && isPending),
  };
}
